"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectKpiWidgetPlaceholderCoordinates = exports.selectKpiWidgetPlaceholder = exports.selectInsightWidgetPlaceholderCoordinates = exports.selectInsightWidgetPlaceholder = exports.selectWidgetPlaceholderCoordinates = exports.selectWidgetPlaceholder = exports.selectWidgetCoordinatesByRef = exports.selectLayoutHasAnalyticalWidgets = exports.selectAllAnalyticalWidgets = exports.selectAllCustomWidgets = exports.selectAllInsightWidgets = exports.selectAllKpiWidgets = exports.selectIsLayoutEmpty = exports.selectAllFiltersForWidgetByRef = exports.selectWidgetDrills = exports.selectAnalyticalWidgetByRef = exports.selectWidgetByRef = exports.selectWidgetsMap = exports.selectWidgets = exports.selectBasicLayout = exports.selectLayout = exports.selectUndoableLayoutCommands = exports.selectStash = void 0;
var tslib_1 = require("tslib");
// (C) 2021-2023 GoodData Corporation
var toolkit_1 = require("@reduxjs/toolkit");
var sdk_model_1 = require("@gooddata/sdk-model");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var layoutTypes_1 = require("../../types/layoutTypes");
var undoEnhancer_1 = require("../_infra/undoEnhancer");
var objRefMap_1 = require("../../../_staging/metadata/objRefMap");
var filterContextSelectors_1 = require("../filterContext/filterContextSelectors");
var converters_1 = require("../../../converters");
var selectors_1 = require("../_infra/selectors");
var widgets_1 = require("../../../widgets");
var layoutUtils_1 = require("./layoutUtils");
var selectSelf = toolkit_1.createSelector(function (state) { return state; }, function (state) { return state.layout; });
/**
 * This selector returns current layout's stash. This stash can contain items that were removed from the layout with the
 * intent of further using the item elsewhere on the layout. The stash is a mapping of stashIdentifier to an array
 * of stashed items. The stash identifiers and stash usage is fully under control of the user.
 *
 * @internal
 */
exports.selectStash = toolkit_1.createSelector(selectSelf, function (layoutState) {
    return layoutState.stash;
});
/**
 * This selector returns commands that impacted the layout and can now be undone.
 *
 * @internal
 */
exports.selectUndoableLayoutCommands = toolkit_1.createSelector(selectSelf, function (layoutState) {
    return undoEnhancer_1.createUndoableCommandsMapping(layoutState);
});
/**
 * This selector returns dashboard's layout. It is expected that the selector is called only after the layout state
 * is correctly initialized. Invocations before initialization lead to invariant errors.
 *
 * @alpha
 */
exports.selectLayout = toolkit_1.createSelector(selectSelf, function (layoutState) {
    ts_invariant_1.default(layoutState.layout, "attempting to access uninitialized layout state");
    return layoutState.layout;
});
/**
 * This selector returns the basic dashboard layout that does not contain any client-side extensions.
 *
 * This selector exists because analytical backend impls are not yet ready to handle persistence of custom
 * widgets (that may have arbitrary payloads). The selector is used only in save and saveAs command handlers,
 * where it obtains the layout without any custom widgets and persists that. Note that the save/saveAs
 * handlers will not wipe the custom widgets from the state during the save - so at this point the custom
 * widgets are treated as client-side extensions.
 *
 * Note: this selector also intentionally removes empty sections; dashboard cannot cope with them and
 * they may readily appear if user adds section full of custom widgets and then does saveAs; such sections
 * would end up empty.
 *
 * @internal
 */
exports.selectBasicLayout = toolkit_1.createSelector(exports.selectLayout, function (layout) {
    var dashboardLayout = tslib_1.__assign(tslib_1.__assign({}, layout), { sections: layout.sections
            .map(function (section) {
            return tslib_1.__assign(tslib_1.__assign({}, section), { items: section.items.filter(layoutUtils_1.isItemWithBaseWidget) });
        })
            .filter(function (section) { return !isEmpty_1.default(section.items); }) });
    return dashboardLayout;
});
/**
 * Selects dashboard widgets in an obj ref an array. This map will include both analytical and custom
 * widgets that are placed on the dashboard.
 *
 * @internal
 */
exports.selectWidgets = toolkit_1.createSelector(exports.selectLayout, function (layout) {
    var e_1, _a, e_2, _b;
    var items = [];
    try {
        for (var _c = tslib_1.__values(layout.sections), _d = _c.next(); !_d.done; _d = _c.next()) {
            var section = _d.value;
            try {
                for (var _e = (e_2 = void 0, tslib_1.__values(section.items)), _f = _e.next(); !_f.done; _f = _e.next()) {
                    var item = _f.value;
                    if (!item.widget) {
                        continue;
                    }
                    items.push(item.widget);
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return items;
});
/**
 * Selects dashboard widgets in an obj ref to widget map. This map will include both analytical and custom
 * widgets that are placed on the dashboard.
 *
 * @internal
 */
exports.selectWidgetsMap = toolkit_1.createSelector(exports.selectWidgets, function (widgets) {
    return objRefMap_1.newMapForObjectWithIdentity(widgets);
});
/**
 * Selects widget by its ref (including custom widgets).
 *
 * @remarks
 * To limit the scope only to analytical widgets, use {@link selectAnalyticalWidgetByRef}.
 *
 * @alpha
 */
exports.selectWidgetByRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectWidgetsMap, function (widgetMap) {
        if (!ref) {
            return undefined;
        }
        return widgetMap.get(ref);
    });
});
/**
 * Selects analytical widget by its ref. This selector will return undefined if the provided
 * widget ref is for a custom widget.
 *
 * @remarks
 * To include custom widgets as well, use {@link selectWidgetByRef}.
 *
 * @alpha
 */
exports.selectAnalyticalWidgetByRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectWidgetsMap, function (widgetMap) {
        if (!ref) {
            return undefined;
        }
        var widget = widgetMap.get(ref);
        if (!widget || layoutTypes_1.isCustomWidget(widget)) {
            return undefined;
        }
        return widget;
    });
});
/**
 * Selects widget drills by the widget ref.
 *
 * @alpha
 */
exports.selectWidgetDrills = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectAnalyticalWidgetByRef(ref), function (widget) { var _a; return (_a = widget === null || widget === void 0 ? void 0 : widget.drills) !== null && _a !== void 0 ? _a : []; });
});
/**
 * Selects all filters from filter context converted to filters specific for a widget specified by a ref.
 *
 * @remarks
 * This does NOT resolve things like ignored filters for a widget, etc.
 *
 * @internal
 */
exports.selectAllFiltersForWidgetByRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectWidgetByRef(ref), filterContextSelectors_1.selectFilterContextFilters, function (widget, dashboardFilters) {
        ts_invariant_1.default(widget, "widget with ref " + sdk_model_1.objRefToString(ref) + " does not exist in the state");
        return converters_1.filterContextItemsToDashboardFiltersByWidget(dashboardFilters, widget);
    });
});
var selectAllWidgets = toolkit_1.createSelector(exports.selectWidgetsMap, function (widgetMap) {
    return Array.from(widgetMap.values());
});
/**
 * Selects a boolean indicating if the dashboard is empty.
 *
 * @alpha
 */
exports.selectIsLayoutEmpty = toolkit_1.createSelector(selectAllWidgets, function (allWidgets) {
    return allWidgets.length === 0;
});
/**
 * Selects all KPI widgets in the layout.
 *
 * @alpha
 */
exports.selectAllKpiWidgets = toolkit_1.createSelector(selectAllWidgets, function (allWidgets) {
    return allWidgets.filter(sdk_model_1.isKpiWidget);
});
/**
 * Selects all insight widgets in the layout.
 *
 * @alpha
 */
exports.selectAllInsightWidgets = toolkit_1.createSelector(selectAllWidgets, function (allWidgets) {
    return allWidgets.filter(sdk_model_1.isInsightWidget);
});
/**
 * Selects all custom widgets in the layout.
 *
 * @alpha
 */
exports.selectAllCustomWidgets = toolkit_1.createSelector(selectAllWidgets, function (allWidgets) {
    return allWidgets.filter(layoutTypes_1.isCustomWidget);
});
/**
 * Selects all non-custom widgets in the layout.
 *
 * @alpha
 */
exports.selectAllAnalyticalWidgets = toolkit_1.createSelector(selectAllWidgets, function (allWidgets) {
    return allWidgets.filter(function (w) { return !layoutTypes_1.isCustomWidget(w); });
});
/**
 * Selects a boolean indicating if the dashboard contains at least one non-custom widget.
 *
 * @alpha
 */
exports.selectLayoutHasAnalyticalWidgets = toolkit_1.createSelector(exports.selectAllAnalyticalWidgets, function (allAnalyticalWidgets) {
    return allAnalyticalWidgets.length > 0;
});
/**
 * Selects layout coordinates for a given widget.
 *
 * @alpha
 */
exports.selectWidgetCoordinatesByRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectLayout, function (layout) {
        var coords = layoutUtils_1.getWidgetCoordinates(layout, ref);
        ts_invariant_1.default(coords, "widget with ref " + sdk_model_1.objRefToString(ref) + " does not exist in the state");
        return coords;
    });
});
/**
 * @internal
 */
exports.selectWidgetPlaceholder = toolkit_1.createSelector(exports.selectAllCustomWidgets, function (customWidgets) {
    return customWidgets.find(widgets_1.isPlaceholderWidget);
});
/**
 * @internal
 */
exports.selectWidgetPlaceholderCoordinates = toolkit_1.createSelector(exports.selectWidgetPlaceholder, exports.selectLayout, function (widgetPlaceholder, layout) {
    return widgetPlaceholder ? layoutUtils_1.getWidgetCoordinates(layout, widgetPlaceholder.ref) : undefined;
});
/**
 * @internal
 */
exports.selectInsightWidgetPlaceholder = toolkit_1.createSelector(exports.selectAllCustomWidgets, function (customWidgets) {
    return customWidgets.find(widgets_1.isInsightPlaceholderWidget);
});
/**
 * @internal
 */
exports.selectInsightWidgetPlaceholderCoordinates = toolkit_1.createSelector(exports.selectInsightWidgetPlaceholder, exports.selectLayout, function (widgetPlaceholder, layout) {
    return widgetPlaceholder ? layoutUtils_1.getWidgetCoordinates(layout, widgetPlaceholder.ref) : undefined;
});
/**
 * @internal
 */
exports.selectKpiWidgetPlaceholder = toolkit_1.createSelector(exports.selectAllCustomWidgets, function (customWidgets) {
    return customWidgets.find(widgets_1.isKpiPlaceholderWidget);
});
/**
 * @internal
 */
exports.selectKpiWidgetPlaceholderCoordinates = toolkit_1.createSelector(exports.selectKpiWidgetPlaceholder, exports.selectLayout, function (widgetPlaceholder, layout) {
    return widgetPlaceholder ? layoutUtils_1.getWidgetCoordinates(layout, widgetPlaceholder.ref) : undefined;
});
//# sourceMappingURL=layoutSelectors.js.map